import { generateMultiCertificate } from '@adornis/buchungsheld-db/api/multiCertificate.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { css, html, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import brandLogo from '../../../assets/logo.png';
import { GlobalSettingsAdapter } from './GlobalSettingsAdapter.js';

@customElement('bh-page-admin-global-settings-general')
export class BHPageAdminGlobalSettingsGeneral extends GlobalSettingsAdapter {
  // eslint-disable-next-line max-lines-per-function
  override render() {
    return html`
      <x-flex space="xl">
        <bh-breadcrumb
          .items=${[
            { content: this._translation.translate('settings', 'name'), href: '/admin/settings' },
            { content: this._translation.translate('settings', 'general_information'), active: true },
          ]}
          .additionalAction=${html`
            <tr-button-small icon="save" .action=${async () => this._formController.validate()}></tr-button-small>
          `}
        ></bh-breadcrumb>

        <x-basic-card padding="md">
          <x-flex space="md">
            <!-- brand Logo -->
            <h2>Logo</h2>
            <x-flex horizontal crossaxis-start wrap space="sm">
              <x-flex
                ${acss({
                  cursor: 'pointer',
                  width: '250px',
                  position: 'relative',
                  '> *': {},
                })}
                @click=${async () => this._handleFileUpload()}
              >
                <tr-tooltip
                  placement="bottom"
                  .text=${this._translation.translate('settings', 'upload_image')}
                ></tr-tooltip>

                <x-image
                  disabled
                  .value=${this._formController.document?.logoID
                    ? AdornisFile.getServeLink(this._formController.document.logoID)
                    : brandLogo}
                  loading="lazy"
                  mode="width"
                >
                </x-image>
              </x-flex>
            </x-flex>
          </x-flex>
        </x-basic-card>

        <x-basic-card padding="md">
          <x-flex space="md">
            <!--Gesamtbescheinigung -->
            <h2>Gesamtbescheinigung Zertifikat Template</h2>
            ${this.globalCertificateVariables()}

            <!-- upload button -->
            <x-flex horizontal crossaxis-start wrap space="sm">
              <x-flex
                ${acss({
                  cursor: 'pointer',
                  width: '250px',
                  position: 'relative',
                  '> *': {},
                })}
                @click=${async () => this._handleCertificateUpload()}
              >
                <tr-button flex icon="check" mode="outline" .color=${this.colors.tone.success} not-clickable>
                  <x-text
                    >${this._formController.document?.multiCertTemplateID
                      ? this._translation.translate('PROMPT', 'file_uploaded')
                      : this._translation.translate('PROMPT', 'upload_crt_tmp')}</x-text
                  >
                  <tr-tooltip
                    placement="bottom"
                    .text=${this._translation.translate('PROMPT', 'upload_crt_tmp')}
                  ></tr-tooltip>
                </tr-button>
              </x-flex>
            </x-flex>

            <!-- preview button -->
            ${this._formController.document?.multiCertTemplateID
              ? html`
                  <tr-button
                    @click=${async () => {
                      const fileID = await XDialog.waitFor(generateMultiCertificate(['testing_1', 'testing_2'])());
                      const link = AdornisFile.getServeLink(fileID);
                      goTo(link + '/test-cert.pdf', { target: '_blank' });
                    }}
                    .color=${this.colors.primary.shade(700)}
                  >
                    Vorschau
                  </tr-button>
                `
              : nothing}
          </x-flex>
        </x-basic-card>
      </x-flex>
    `;
  }

  globalCertificateVariables() {
    const variableNames = ['vorname', 'nachname', 'event-namen'];
    return html`
      <x-toggle-content .label=${'Mögliche Variablen: '} .open=${true}>
        <x-flex space="sm" ${css({ color: this.colors.neutral })}>
          <x-flex space="xs">
            <x-flex horizontal wrap space="xs">
              ${variableNames.map(
                variableName =>
                  html`
                    <x-chip
                      title="Klicken zum kopieren"
                      ${css({ cursor: 'pointer' })}
                      @click=${async () => {
                        try {
                          await navigator.clipboard.writeText(`{{${variableName}}}`);
                          await XSnackbar.show('Variable kopiert');
                        } catch {}
                      }}
                    >
                      ${variableName}
                    </x-chip>
                  `,
              )}
            </x-flex>
          </x-flex>
          <x-text> Variablen müssen im folgendem Format angegeben werden: {{vorname}} </x-text>
        </x-flex>
      </x-toggle-content>
    `;
  }

  private async _handleFileUpload() {
    if (!this._formController.document) return;
    const files = await askForFile({
      acceptFileTypes: '.png, .img, .jpeg, .jpg',
      acceptMultipleFiles: false,
    });
    if (!files) return;
    const file = files[0];
    if (!file) return;

    const fileID = await AdornisFile.upload(file);
    this._formController.document.logoID = fileID;
    this.requestUpdate();
  }

  private async _handleCertificateUpload() {
    if (!this._formController.document) return;

    const files = await askForFile({ acceptFileTypes: '.docx', acceptMultipleFiles: false });
    if (!files) return;
    const file = files[0];
    if (!file) return;

    const fileID = await XDialog.waitFor(AdornisFile.upload(file));
    this._formController.document.multiCertTemplateID = fileID;
    this.requestUpdate();
  }
}
